import {
  IonButtons,
  IonModal,
  IonTitle,
  IonToolbar,
  IonHeader,
  IonContent,
  IonItem,
  IonIcon,
  IonText,
  IonButton,
} from '@ionic/react';
import HStack from 'components/HStack';
import VStack from 'components/VStack';
import DynamicButton from 'components/buttons/DynamicButton';
import {
  calendarOutline,
  locationOutline,
  flagOutline,
  peopleOutline,
} from 'ionicons/icons';
import { ComponentProps, FunctionComponent, useRef, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ExploreElement } from '../../models/ExploreElementModel/ExploreElementModel';
import { useStores } from '../../models';
import './EventModal.css';

export interface EventModalProps extends ComponentProps<typeof IonModal> {
  isOpen: boolean;
  onDidDismiss: () => void;
  event: ExploreElement | null;
  onEditElement?: (element: ExploreElement) => void;
  onDeleteElement?: (element: ExploreElement) => void;
}

const EventModal: FunctionComponent<EventModalProps> = ({
  isOpen,
  onDidDismiss,
  event,
  onEditElement,
  onDeleteElement,
  ...rest
}) => {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLIonModalElement>(null);
  const { currentUser } = useStores();

  const handleClose = () => {
    onDidDismiss?.();
    modalRef.current?.dismiss();
  };

  // Prüfe, ob der aktuelle Benutzer der Besitzer des Events ist
  const isOwner = event?.isOwnedByUser(currentUser?.uid);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <IonModal
      ref={modalRef}
      isOpen={isOpen}
      onDidDismiss={onDidDismiss}
      className="edit-modal"
      {...rest}
    >
      <IonHeader className="ion-no-border">
        {/* <div className="modal-image-svg">
          <img src={'/assets/images/Pic_Continent_Filter.svg'} alt="Header" />
        </div> */}
        <IonToolbar className="edit-modal-toptoolbar">
          {isOwner && (
            <IonButtons slot="start">
              <DynamicButton
                text={<Trans i18nKey="DELETE_EVENT" />}
                iconName="DeleteUpIcon"
                buttonType="icon-full"
                size="xlarge"
                fill="clear"
                color="danger"
                className="spnx-button"
                onClick={() => event && onDeleteElement?.(event)}
              />
              <DynamicButton
                text={<Trans i18nKey="EDIT_EVENT" />}
                iconName="AddUpIcon"
                buttonType="icon-full"
                size="xlarge"
                fill="clear"
                className="spnx-button"
                onClick={() => event && onEditElement?.(event)}
              />
            </IonButtons>
          )}

          <IonButtons slot="end">
            <DynamicButton
              text={<Trans i18nKey="CANCEL" />}
              iconName="CancelUpIcon"
              buttonType="icon-full"
              size="xlarge"
              fill="clear"
              className="spnx-button"
              onClick={handleClose}
            />
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent
        style={{ '--background': 'transparent', '--border-colour': 0 }}
      >
        {event ? (
          <div className="event-modal-wrapper">
            <div className="event-modal-header">
              <h1>{event.name}</h1>
            </div>

            <div className="event-modal-details">
              {/* Datum */}
                <HStack className="event-detail-item">
                <IonIcon icon={calendarOutline} style={{ color: 'white' }} />
                <IonText>{formatDate(event.startDate)}</IonText>
                </HStack>

              {/* Ort wenn vorhanden */}
              {event.location && (
                <HStack className="event-detail-item">
                  <IonIcon icon={locationOutline} style={{ color: 'white' }} />
                  <IonText>{event.location}</IonText>
                </HStack>
              )}

              {/* Land wenn vorhanden */}
              {event.country && (
                <HStack className="event-detail-item">
                  <IonIcon icon={flagOutline} style={{ color: 'white' }} />
                  <IonText>{event.country}</IonText>
                </HStack>
              )}

              {/* Teilnehmerinfos, falls diese hinzugefügt werden */}
              {false && (
                <HStack className="event-detail-item">
                  <IonIcon icon={peopleOutline} />
                  <IonText>0 Teilnehmer</IonText>
                </HStack>
              )}
            </div>

            {event.description && (
              <div className="event-modal-description">
                <h2>
                  <Trans i18nKey="DESCRIPTION" />
                </h2>
                <p>
                  <Trans i18nKey="NO_DESCRIPTION_AVAILABLE">
                    {event.description}
                  </Trans>
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="event-modal-empty">
            <p>{t('NO_EVENT_SELECTED')}</p>
          </div>
        )}
      </IonContent>
    </IonModal>
  );
};

export default EventModal;
